// client/components/evaluate/EvidencePanel.tsx
// Task 4.4 — Expandable evidence body (signals, threshold, fallbacks, SHACL link)
import React, { useState } from 'react';
import type { EvidencePayload, MaturityBreakdown } from '../../hooks/useEvaluateData';

interface EvidencePanelProps {
  evidence: EvidencePayload;
  expanded: boolean;
  /** Human-readable narrative for the rule — what the engine concluded.
   *  Surfaced as a "Note" block at the top of the expanded panel so SHACL
   *  passes (which carry only a shape link) still convey context. Mirrors
   *  the same narrative the PDF report renders. */
  noteText?: string;
  /** When true, shows a one-line courtesy banner explaining that this
   *  rule was analyzed by a pre-v1.1.8 engine that didn't emit per-
   *  property signals on a passing SHACL shape. Re-running unlocks the
   *  rich lineage. Set by EvaluateRuleCard for F2/F3/A1/R1 PASSes that
   *  carry no signals + no fallbacks + no maturity rubric. */
  showLegacyShaclPassNote?: boolean;
}

export function EvidencePanel({ evidence, expanded, noteText, showLegacyShaclPassNote }: EvidencePanelProps) {
  const [showShacl, setShowShacl] = useState(false);
  if (!expanded) return null;

  return (
    <div style={{ marginTop: 10, paddingTop: 10, borderTop: '1px dashed var(--tri-taupe)' }}>
      {noteText && (
        <Section label="Note">
          <div
            style={{
              padding: '8px 10px',
              background: 'var(--tri-card-2)',
              borderLeft: '2px solid var(--tri-teal)',
              borderRadius: 4,
              color: 'var(--tri-body)',
              fontSize: '0.78rem',
              lineHeight: 1.5,
            }}
          >
            {noteText}
          </div>
        </Section>
      )}

      {evidence.maturity_breakdown && (
        <MaturityLadder breakdown={evidence.maturity_breakdown} />
      )}

      {(evidence.signals?.length ?? 0) > 0 && (
        <Section label="Signals">
          {evidence.signals?.map((s, i) => (
            <Row key={i} label={s.type}>
              <span style={{ color: 'var(--tri-teal)' }}>{s.location}</span>
              {' = '}
              <span style={{ color: 'var(--tri-neutral)' }}>{s.value}</span>
              {' '}<span style={{ color: 'var(--tri-slate)', fontStyle: 'italic' }}>({s.role})</span>
            </Row>
          ))}
        </Section>
      )}

      {(evidence.threshold || evidence.measured) && (
        <Section label="Threshold">
          {evidence.threshold && <Row label="required">{evidence.threshold}</Row>}
          {evidence.measured && <Row label="measured"><span style={{ color: 'var(--tri-terra)' }}>{evidence.measured}</span></Row>}
        </Section>
      )}

      {evidence.fallbacks_tried && evidence.fallbacks_tried.length > 0 && (
        <Section label="Fallbacks tried">
          {evidence.fallbacks_tried.map((fb, i) => (
            <Row key={i} label={fb.method}>{fb.outcome}</Row>
          ))}
        </Section>
      )}

      {showLegacyShaclPassNote && (
        <div
          style={{
            marginTop: 8,
            padding: '8px 10px',
            background: 'var(--tri-soft-bg)',
            borderLeft: '2px solid var(--tri-soft)',
            borderRadius: 4,
            color: 'var(--tri-body)',
            fontSize: '0.75rem',
            lineHeight: 1.5,
          }}
        >
          <strong style={{ color: 'var(--tri-soft)' }}>Per-property lineage available in v1.1.8+:</strong>
          {' '}this rule was analyzed by an earlier engine that didn't emit per-property
          signals on a passing SHACL shape. Re-run the FAIR analysis to see what each
          shape constraint examined on this dataset.
        </div>
      )}

      {evidence.shacl_shape_link && (
        <div style={{ marginTop: 8 }}>
          <button
            type="button"
            onClick={() => setShowShacl(!showShacl)}
            style={{
              background: 'transparent',
              border: 0,
              color: 'var(--tri-teal)',
              fontSize: '0.75rem',
              textDecorationLine: 'underline',
              textDecorationStyle: 'dotted',
              cursor: 'pointer',
              padding: 0,
            }}
          >
            {showShacl ? '▾ Hide SHACL shape' : '▸ Show SHACL shape'}
          </button>
          {showShacl && (
            <Row label="shape">
              <span style={{ color: 'var(--tri-neutral)' }}>{evidence.shacl_shape_link.shape_name}</span>
              {evidence.shacl_shape_link.source_path && (
                <span style={{ color: 'var(--tri-slate)' }}> · {evidence.shacl_shape_link.source_path}</span>
              )}
            </Row>
          )}
        </div>
      )}
    </div>
  );
}

/**
 * Renders the F2 element-list maturity rubric. Each tier is shown as a row;
 * the achieved tier is highlighted, and below it the per-element ✓/✗ grid
 * exposes which elements landed and which are missing — so the human reviewer
 * sees exactly *why* the machine assigned its tier without re-deriving the
 * rubric from prose.
 */
function MaturityLadder({ breakdown }: { breakdown: MaturityBreakdown }) {
  const present = new Set(breakdown.elements_present);
  const achieved = breakdown.tier_achieved;

  return (
    <div style={{ marginBottom: 12 }}>
      <span
        style={{
          display: 'inline-block',
          padding: '2px 6px',
          background: 'var(--tri-neutral-bg)',
          color: 'var(--tri-neutral)',
          borderRadius: 4,
          fontSize: '0.6rem',
          fontWeight: 700,
          letterSpacing: '0.04em',
          textTransform: 'uppercase',
        }}
      >
        Maturity tiers
      </span>
      <div
        style={{
          marginTop: 6,
          display: 'grid',
          gap: 4,
        }}
      >
        {breakdown.tier_definitions.map((def) => {
          const reached = achieved >= def.tier;
          return (
            <div
              key={def.tier}
              style={{
                display: 'grid',
                gridTemplateColumns: '38px 110px 1fr',
                gap: 8,
                alignItems: 'center',
                padding: '6px 8px',
                background: reached ? 'var(--tri-good-bg)' : 'var(--tri-card-2)',
                borderLeft: reached ? '2px solid var(--tri-good)' : '2px solid var(--tri-taupe)',
                borderRadius: 4,
                fontSize: '0.72rem',
                color: reached ? 'var(--tri-ink)' : 'var(--tri-slate)',
              }}
            >
              <span style={{ fontWeight: 700, color: reached ? 'var(--tri-good)' : 'var(--tri-slate)' }}>
                T{def.tier}
              </span>
              <span style={{ color: reached ? 'var(--tri-body)' : 'var(--tri-slate)' }}>{def.label}</span>
              <span
                style={{
                  display: 'flex',
                  flexWrap: 'wrap',
                  gap: 4,
                  fontFamily: 'JetBrains Mono, Consolas, monospace',
                  fontSize: '0.66rem',
                }}
              >
                {def.required_elements.map((el) => (
                  <span
                    key={el}
                    title={present.has(el) ? `${el}: present` : `${el}: missing`}
                    style={{
                      padding: '1px 6px',
                      borderRadius: 3,
                      background: present.has(el)
                        ? 'var(--tri-good-bg)'
                        : 'var(--tri-hard-bg)',
                      color: present.has(el) ? 'var(--tri-good)' : 'var(--tri-hard)',
                      border: `1px solid ${
                        present.has(el) ? 'var(--tri-good)' : 'var(--tri-hard-line)'
                      }`,
                    }}
                  >
                    {present.has(el) ? '✓' : '✗'} {el}
                  </span>
                ))}
              </span>
            </div>
          );
        })}
      </div>
      {achieved < breakdown.tier_max && (
        <div style={{ marginTop: 6, fontSize: '0.7rem', color: 'var(--tri-slate)' }}>
          To advance to Tier {achieved + 1}, populate:{' '}
          <span style={{ color: 'var(--tri-terra)', fontFamily: 'JetBrains Mono, Consolas, monospace' }}>
            {breakdown.tier_definitions
              .find((d) => d.tier === achieved + 1)
              ?.required_elements.filter((e) => !present.has(e))
              .join(', ')}
          </span>
        </div>
      )}
    </div>
  );
}

function Section({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div style={{ marginTop: 8 }}>
      <span
        style={{
          display: 'inline-block',
          padding: '2px 6px',
          background: 'var(--tri-neutral-bg)',
          color: 'var(--tri-neutral)',
          borderRadius: 4,
          fontSize: '0.6rem',
          fontWeight: 700,
          letterSpacing: '0.04em',
          textTransform: 'uppercase',
        }}
      >
        {label}
      </span>
      <div style={{ marginTop: 4 }}>{children}</div>
    </div>
  );
}

function Row({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div
      style={{
        display: 'grid',
        gridTemplateColumns: '110px 1fr',
        gap: 10,
        padding: '4px 8px',
        margin: '3px 0',
        background: 'var(--tri-card-2)',
        borderRadius: 4,
        fontFamily: 'JetBrains Mono, Consolas, monospace',
        fontSize: '0.72rem',
        color: 'var(--tri-ink)',
      }}
    >
      <span style={{ color: 'var(--tri-slate)', textTransform: 'uppercase', fontSize: '0.62rem', letterSpacing: '0.04em', alignSelf: 'center' }}>{label}</span>
      <span>{children}</span>
    </div>
  );
}
